import { useEffect, useRef } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import {
  ONBOARDING_INTAKE_FLOW_ID,
  hasCompatibleUsageCapture,
  readAnswer,
  readReportedWeek,
  USAGE_CAPTURE_KEY,
  selectOnboardingUsageProgram,
  type OnboardingUsageConfig,
  type ReportedBaselineWeek,
  type UsageCaptureContext,
} from '@galapagos/shared';
import { useCatalogWords } from '../hooks/useCatalogWords';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { SignupNotImplementedError, type SignupApi } from '../services/signup-api/signup-api';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { nextSignupStep, SIGNUP_STEPS, type SignupStepId } from '../steps';
import { LAST_BASELINE_STEP } from './baseline-steps';
import { wordedBaseline, type BaselineWording } from './baseline-wording';

/**
 * The step after the segment, so whichever step follows it (today the TAPS 2
 * placeholder) takes over without this segment changing.
 */
export function baselineExitStep(): SignupStepId {
  const exit = SIGNUP_STEPS[SIGNUP_STEPS.indexOf(LAST_BASELINE_STEP) + 1];
  if (exit === undefined) throw new Error('The signup baseline must be followed by another step');
  return exit;
}

/** Where a step goes when the implementation has no content and it passes through. */
export function baselinePassThroughStep(step: SignupStepId): SignupStepId {
  if (step === 'baseline-week') return baselineExitStep();
  return nextSignupStep(step) ?? baselineExitStep();
}

export type BaselineRead<T> =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'error'; retry: () => void }
  | { status: 'ready'; data: T };

/**
 * The baseline content's structure. No retry: the content is served from the
 * bundle, so a failure would not heal on a second try. It never changes within
 * a page, so a copy loaded ahead of the step is used as is.
 */
function baselineContentQuery(api: SignupApi, scenario: string | null) {
  return queryOptions({
    queryKey: ['signup', 'baseline-content', scenario],
    queryFn: () => api.getBaselineContent(),
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * The usage config for every program. It changes only with a deploy or a
 * content edit, so a copy loaded ahead of the step is not read again.
 */
function usageConfigQuery(api: SignupApi, scenario: string | null) {
  return queryOptions({
    queryKey: ['signup', 'usage-config', scenario],
    queryFn: () => api.getOnboardingUsageConfig(),
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * Starts the last-week step's content and usage config reads ahead of it,
 * while `enabled`, so the step can draw its table on arrival. Fire and
 * forget: a failed read leaves no data, and the step reads again itself.
 */
export function usePrefetchBaselineReads(enabled: boolean): void {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    void queryClient.prefetchQuery(usageConfigQuery(api, scenario));
    void queryClient.prefetchQuery(baselineContentQuery(api, scenario));
  }, [enabled, api, scenario, queryClient]);
}

/**
 * The wording for the member's primary program. An implementation with no
 * content at all passes the step through.
 */
export function useBaselineWording(): BaselineRead<BaselineWording> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const programSelection = useSignupFlowStore((state) => state.programSelection);
  // One structure serves every language; a language switch re-renders the
  // words from the catalog, so the step keeps its counts.
  const words = useCatalogWords();
  const query = useQuery(baselineContentQuery(api, scenario));
  if (query.isPending) return { status: 'loading' };
  if (query.isError) {
    if (query.error instanceof SignupNotImplementedError) return { status: 'unavailable' };
    return { status: 'error', retry: () => void query.refetch() };
  }
  return { status: 'ready', data: wordedBaseline(words, query.data, programSelection) };
}

/** The intake session's cache key, shared by the steps' reads and the save. */
export function sessionQueryKey(scenario: string | null) {
  return ['signup', 'onboarding-session', ONBOARDING_INTAKE_FLOW_ID, scenario] as const;
}

/** How the member's primary program asks its week: a count per day, or used or not. */
export type BaselineCapture =
  /** `program` when the usage config counts the member's program, so a save can claim its capture. */
  | { mode: 'counter'; program?: UsageCaptureContext }
  | { mode: 'yes-no'; program: UsageCaptureContext };

/**
 * The capture for the member's primary program, picked the way finalize
 * picks it: the selection's program keys (never a sub-type), ranked by the
 * shared primary-program rule. Only a `yes-no` program changes the steps;
 * any other, or none, keeps the count per day.
 */
export function captureFor(
  config: OnboardingUsageConfig,
  programSelection: readonly string[] | null,
): BaselineCapture {
  const keys = new Set(config.programs.map((program) => program.programKey));
  const programKeys = (programSelection ?? []).filter((value) => keys.has(value));
  const program = selectOnboardingUsageProgram(config.programs, { program_key: programKeys });
  if (program?.captureMode === 'yes-no') return { mode: 'yes-no', program };
  return program?.captureMode === 'counter' ? { mode: 'counter', program } : { mode: 'counter' };
}

/**
 * The capture for the member's primary program, from the usage config the
 * app also reads. An implementation with no usage config keeps the count per
 * day; any other failure offers a retry.
 */
export function useBaselineCapture(): BaselineRead<BaselineCapture> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const programSelection = useSignupFlowStore((state) => state.programSelection);
  const query = useQuery(usageConfigQuery(api, scenario));
  if (query.isPending) return { status: 'loading' };
  if (query.isError) {
    if (query.error instanceof SignupNotImplementedError) {
      return { status: 'ready', data: { mode: 'counter' } };
    }
    return { status: 'error', retry: () => void query.refetch() };
  }
  return { status: 'ready', data: captureFor(query.data, programSelection) };
}

/** The intake session's answers, read fresh once per step. */
export function useBaselineSession(): BaselineRead<Record<string, unknown>> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const query = useQuery({
    queryKey: sessionQueryKey(scenario),
    queryFn: () => api.getOnboardingSession(ONBOARDING_INTAKE_FLOW_ID),
    retry: false,
    // Read once per step: a re-read on focus would blank a form mid-edit.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // A cached session can be older than the member's last save; the step
  // waits for the fresh read rather than acting on the old one.
  if (query.isPending || query.isFetching) return { status: 'loading' };
  if (query.isError) return { status: 'error', retry: () => void query.refetch() };
  return { status: 'ready', data: query.data.answers };
}

/**
 * The week saved in `answers`, or null when none is complete. A yes/no week
 * also needs the week saved under its own capture, and a counted week must
 * not sit under another program's or another mode's capture: either kind is
 * asked again, so the next save replaces what would refuse it at finalize.
 */
export function savedWeekFor(
  answers: Record<string, unknown>,
  capture: BaselineCapture,
): ReportedBaselineWeek | null {
  const program = capture.program;
  if (program) {
    const held = readAnswer(answers, USAGE_CAPTURE_KEY);
    const mustMatch = capture.mode === 'yes-no' || (held !== undefined && held !== null);
    if (mustMatch && !hasCompatibleUsageCapture(answers, program)) return null;
  }
  return readReportedWeek(answers);
}

/** Everything the typical and adjust steps read, as one read. */
export interface BaselineReads {
  wording: BaselineWording;
  capture: BaselineCapture;
  week: ReportedBaselineWeek | null;
  answers: Record<string, unknown>;
}

/**
 * The wording, the capture and the saved session as one read: an
 * implementation with no content passes the step through; otherwise any read
 * still loading, then any failing, decides.
 */
export function useBaselineReads(): BaselineRead<BaselineReads> {
  const wording = useBaselineWording();
  const capture = useBaselineCapture();
  const session = useBaselineSession();
  if (wording.status === 'unavailable') return wording;
  for (const read of [wording, capture, session]) {
    if (read.status === 'loading') return { status: 'loading' };
  }
  for (const read of [wording, capture, session]) {
    if (read.status === 'error' || read.status === 'unavailable') return read;
  }
  if (wording.status !== 'ready' || capture.status !== 'ready' || session.status !== 'ready') {
    return { status: 'loading' };
  }
  return {
    status: 'ready',
    data: {
      wording: wording.data,
      capture: capture.data,
      week: savedWeekFor(session.data, capture.data),
      answers: session.data,
    },
  };
}

/** What the last-week step reads: the wording and the capture, with the session's own read. */
export interface BaselineWeekReads {
  wording: BaselineWording;
  capture: BaselineCapture;
  /**
   * The session's answers, which only the save needs; ready and empty when the
   * program has no capture, which saves over nothing.
   */
  session: BaselineRead<Record<string, unknown>>;
}

/**
 * The last-week step's reads: ready once the wording and the capture are, so
 * the table draws while the session is still read. The session matters only
 * to the save (it clears another capture's answers), and a count per day with
 * no known program never waits on it at all.
 */
export function useBaselineWeekReads(): BaselineRead<BaselineWeekReads> {
  const wording = useBaselineWording();
  const capture = useBaselineCapture();
  const session = useBaselineSession();
  if (wording.status === 'unavailable') return wording;
  if (wording.status === 'loading' || capture.status === 'loading') return { status: 'loading' };
  if (wording.status === 'error') return wording;
  if (capture.status !== 'ready') return capture;
  const savesOverNothing = capture.data.mode === 'counter' && capture.data.program === undefined;
  return {
    status: 'ready',
    data: {
      wording: wording.data,
      capture: capture.data,
      session: savesOverNothing ? { status: 'ready', data: {} } : session,
    },
  };
}

/**
 * A later step reached with no complete week saved (a fresh session, or a
 * link straight to it) goes back to the week step, replacing itself. Only once
 * both reads have landed: content with no backend passes the step through
 * instead.
 */
export function useReturnWhenWeekMissing(
  reads: BaselineRead<{ week: ReportedBaselineWeek | null }>,
): void {
  const { goToStep } = useSignupNavigation();
  const missing = reads.status === 'ready' && reads.data.week === null;
  useEffect(() => {
    if (missing) goToStep('baseline-week');
  }, [missing, goToStep]);
}

/** Baseline answers as saved: counts and anchors, and for a yes/no week its choices, clears and capture. */
export type BaselineAnswers = Record<string, unknown>;

export interface BaselineSave {
  /** Saves once, then moves to `next`. Presses while a save is pending do nothing. */
  save: (answers: BaselineAnswers, next: SignupStepId) => void;
  pending: boolean;
  failed: boolean;
}

/**
 * One blocking save of baseline answers to the intake session (ADR-058): never
 * optimistic, never retried on its own. A failure leaves the member's values
 * on screen; their next press saves again.
 */
export function useBaselineSave(): BaselineSave {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const queryClient = useQueryClient();
  const { goToStep } = useSignupNavigation();
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: (answers: BaselineAnswers) =>
      api.answerOnboarding(ONBOARDING_INTAKE_FLOW_ID, { answers }),
    retry: false,
    // The next step reads the session; it gets the answers just saved.
    onSuccess: (session) => queryClient.setQueryData(sessionQueryKey(scenario), session),
  });
  const save = (answers: BaselineAnswers, next: SignupStepId) => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(answers, {
      onSuccess: () => goToStep(next),
      onError: () => {
        inFlight.current = false;
      },
    });
  };
  return { save, pending: mutation.isPending, failed: mutation.isError };
}
